import { Environment, useGLTF } from '@react-three/drei'
import { TalkingHeadAvatar } from './TalkingHeadAvatar'
import React, { useEffect, useState, useRef } from 'react'
import { Group, Mesh, Object3D, PerspectiveCamera } from 'three'
import { useThree } from '@react-three/fiber'
import { GLTFResult } from './types'

import LeoraModel from '../../assets/models/leora.glb?url'
import EnvironmentSceen from '../../assets/venice_sunset_1k.hdr?url'

interface TalkingHeadSceneProps {
  scale: number;
  onFirstFrame?: () => void;
}

export const TalkingHeadScene = React.memo(({ scale, onFirstFrame }: TalkingHeadSceneProps) => {
  // Model initialization & garbage collection
  // TODO: make it work properly, there is a decent memory leak
  const avatarRef = useRef<Group>()
  const previousScene = useRef<Group | null>()
  let triggered = false;

  const model = useGLTF(LeoraModel) as GLTFResult

  const destructObjectsTree = (scene: Object3D) => {
    scene.traverse((child: any) => {
      if ((child as Mesh).geometry) (child as Mesh).geometry.dispose()
      if ((child as Mesh).material) {
        const material = (child as Mesh).material

        if (Array.isArray(material)) {
          material.forEach((item) => item.dispose())
        } else {
          material.dispose()
        }
      }
      if ((child as any).dispose) (child as any).dispose()
    });
  }

  useEffect(() => {
    if (previousScene.current) {
      destructObjectsTree(previousScene.current)
      previousScene.current = null
    }

    // Store current values
    previousScene.current = model.scene;

    const markAfterRender = () => {
      if (triggered) return;
      triggered = true;
      onFirstFrame?.();
    };

    model.scene.traverse((object: THREE.Object3D) => {
      object.onAfterRender = markAfterRender as any;
    });
  }, [model]);

  // Camera
  const { size, set: setCamera } = useThree()
  const lastCameraRef = useRef<PerspectiveCamera | null>(null);

  useEffect(() => {
    const activeCamera = (model.cameras?.[0] as PerspectiveCamera | undefined);
    if (!activeCamera) return;
  
    if (lastCameraRef.current !== activeCamera) {
      activeCamera.aspect = size.width / size.height;
      activeCamera.updateProjectionMatrix();
      (activeCamera as any).manual = true;
  
      setCamera({ camera: activeCamera });
      lastCameraRef.current = activeCamera;
    } else {
      activeCamera.aspect = size.width / size.height;
      activeCamera.updateProjectionMatrix();
    }
  }, [model.cameras, size.width, size.height, setCamera]);

  const modelCamera = model.cameras[0]
  const defaultInitialCameraPosition = { x: 0, y: -1.4, z: 4 }
  const defaultInitialCameraRotation = { x: 0, y: -0.4, z: 0 }

  const initialPosition = modelCamera
    ? [modelCamera.position.x, modelCamera.position.y, modelCamera.position.z]
    : [defaultInitialCameraPosition.x, defaultInitialCameraPosition.y, defaultInitialCameraPosition.z]

  const initialRotation = modelCamera
    ? [modelCamera.rotation.x, modelCamera.rotation.y, modelCamera.rotation.z]
    : [defaultInitialCameraRotation.x, defaultInitialCameraRotation.y, defaultInitialCameraRotation.z]

  const [cameraPosition] = useState(defaultInitialCameraPosition);
  const [cameraRotation, setCameraRotation] = useState(defaultInitialCameraRotation);

  useEffect(
    () => console.log(`Camera position: x=${cameraPosition.x}, y=${cameraPosition.y}, z=${cameraPosition.z}`),
    [cameraPosition]
  );

  useEffect(
    () => console.log(`Camera rotation: x=${cameraRotation.x}, y=${cameraRotation.y}, z=${cameraRotation.z}`),
    [cameraRotation]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          setCameraRotation(({ x, y, z }) => {
            return { x: x + 0.1, y, z }
          })
          break
        case 'ArrowRight':
          setCameraRotation(({ x, y, z }) => {
            return { x, y: y + 0.1, z }
          })
          break
        case 'ArrowDown':
          setCameraRotation(({ x, y, z }) => {
            return { x: x - 0.1, y, z }
          })
          break
        case 'ArrowLeft':
          setCameraRotation(({ x, y, z }) => {
            return { x, y: y - 0.1, z }
          })
          break
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <Environment files={EnvironmentSceen} />
      <TalkingHeadAvatar
        key={1}
        avatarRef={avatarRef}
        position={initialPosition}
        rotation={initialRotation}
        scale={scale}
        model={model}
        modelVersion={1}/>
    </>
  )
})

try {} catch(e) {
  console.log("Error on TalkingHeadAvatars loading!")
  console.log((e as Error)?.message);
}
